import { VideoPoster, WatchProgressStripe } from "@client/components/video";
import { resolveVideoPosterUrl } from "@client/utils/videoPosterUrl";
import { Text, Button } from "@sfc/ui";
import { PlayIcon } from "lucide-react";
import { type TodoStep } from "@lib/todoRail/todoRailModel";

/**
 * The highlighted card at the top of a tab.
 *
 * Shared by both tabs but told what it is by its caller, because the two are
 * genuinely different claims: To Do leads with "Next on the list" (the plan),
 * History leads with "Pick up where you left off" (the record). One card
 * deciding its own meaning from state is how it ended up offering to resume a
 * step the funder had just finished.
 */
export function TodoResumeCard({
  title,
  detail,
  step,
  eyebrow,
  action,
  disabled,
  onOpen,
}: {
  title: string;
  detail: string;
  /**
   * The matching rail step, when there is one — it supplies the thumbnail and
   * the watch stripe. History can point at things the rail has no step for (a
   * proposal, the Decide grid), so this is optional and the card degrades to
   * title + detail.
   */
  step?: TodoStep | null;
  eyebrow: string;
  action: string;
  disabled?: boolean;
  onOpen: () => void;
}) {
  const video = step?.video ?? null;
  // The SAME number the spine row draws — `step.progress.fraction`, not a
  // second derivation from the playhead. Recomputing it here is what made the
  // thumbnail stripe disagree with the rail.
  const watchFraction = step?.progress.fraction ?? 0;
  const posterUrl =
    video == null
      ? undefined
      : resolveVideoPosterUrl({
          videoId: video.videoId,
          hasPoster: video.posterId != null,
          posterId: video.posterId,
          posterCdnUrl: video.posterCdnUrl,
        });

  return (
    <div className="mx-6 flex flex-col gap-2.5 rounded-lg border border-primary bg-accent-subtle p-3">
      <Text
        as="span"
        variant="label-sm"
        className="flex items-center gap-1.5 tracking-wider text-primary uppercase"
      >
        <PlayIcon className="size-3 fill-current" />
        {eyebrow}
      </Text>
      <div className="flex items-center gap-2.5">
        {video != null && (
          <VideoPoster
            posterUrl={posterUrl}
            className="aspect-video w-19 flex-none rounded-md"
          >
            <WatchProgressStripe watchFraction={watchFraction} />
          </VideoPoster>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <Text
            as="span"
            variant="body-sm"
            className="leading-snug font-medium break-words"
          >
            {title}
          </Text>
          <Text
            as="span"
            variant="body-sm"
            tone="secondary"
            className="tabular-nums"
          >
            {detail}
          </Text>
        </div>
        <Button size="sm" onClick={onOpen} disabled={disabled}>
          {action}
        </Button>
      </div>
    </div>
  );
}
